import type { Page } from 'types';
import type { BlockSignupForm } from 'types/block-signup-form';
import { Card, Wrap } from '@leafwell/components';
import Image from 'next/image';
import HeadingBody from 'components/heading-body';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import SignupForm, { SignupFormProps } from './signup-form';

const AffiliateLogo = dynamic(() => import('./affiliate-logo'), {
  loading: () => <div className="h-28"></div>,
  ssr: false,
});

export type SignupFormBlockProps = Omit<BlockSignupForm, 'acfFcLayout'> &
  Omit<SignupFormProps, 'hero'> & {
    affiliateLogo?: Page['acf']['affiliateLogo'];
    affiliateWebsite?: Page['acf']['affiliateWebsite'];
    hasReferral?: Page['acf']['hasReferral'];
  };

const SignupFormBlock: React.FC<SignupFormBlockProps> = ({
  title,
  formDescription,
  address,
  sectionId,
  affiliateLogo,
  affiliateWebsite,
  ...props
}) => {
  return (
    <Wrap id={sectionId} tagName="div" size="large">
      <Card
        className="lg:grid-cols-12 lg:gap-x-16 xl:gap-x-24 gap-y-16 md:gap-y-16 lg:gap-y-0"
        filled={true}
        rounded={true}
        size="large"
        tagName="section"
        variant="light"
      >
        <div className="grid gap-y-6 lg:gap-y-10 lg:col-span-6 content-start justify-items-center lg:justify-items-start">
          <AffiliateLogo
            affiliateLogo={affiliateLogo}
            affiliateWebsite={affiliateWebsite}
          />
          <HeadingBody
            tagName="h2"
            className={[
              'font-sans my-reset prose text-center lg:text-left',
              '!text-3xl xl:!text-6xl',
            ].join(' ')}
            title={title}
          />
          {formDescription?.length > 0 ? (
            <div
              className="text-lg md:text-xl xl:text-2xl text-center self-center lg:self-start lg:text-left max-w-sm lg:max-w-[470px] lw-text-section"
              dangerouslySetInnerHTML={{ __html: formDescription }}
            ></div>
          ) : null}
          {address?.length > 0 ? (
            <div className="lg:flex items-start text-center lg:text-left">
              <Image
                className="inline lg:mt-2 mr-2"
                src="/location.svg"
                alt=""
                width={16}
                height={16}
              />
              <Link
                className="xl:text-lg text-center self-center lg:self-start lg:text-left max-w-sm lg:max-w-md underline"
                href={`https://www.google.com/maps?q=${address}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {address}
              </Link>
            </div>
          ) : null}
        </div>
        <div className="grid justify-self-center lg:justify-self-end lg:col-span-6 gap-y-3 max-w-md">
          <SignupForm redirectOnStateChange={false} {...props} />
          <div className="my-2 text-center">
            <p className="text-sm">
              Already a Leafwell patient?
              <br />
              <a
                className="font-medium underline underline-offset-3"
                target="_blank"
                href={process.env.NEXT_PUBLIC_LOGIN_URL}
              >
                Log in here
              </a>
            </p>
          </div>
        </div>
      </Card>
    </Wrap>
  );
};

export default SignupFormBlock;
